import {
    createSimpleGetter,
    createSimpleMutator,
} from '@superbet-group/web.lib.vue-utilities';
import {
    ActionContext,
    Commit,
    Dispatch,
} from 'vuex';

import {
    CloseAccountMfaPayload,
    ReasonPayload,
} from '@core/services/user/_private/Types';
import {
    cancelLimit,
    closeAccount,
    closeAccountMfa,
    getLimits,
    selfExclude,
    setLimits,
    timeOut,
} from '@core/services/user/limits/limitsService';
import {
    LimitType,
    TimeType,
    LimitData,
} from '@models/user/Limit';
import { asyncErrorGuard } from '@utils/ErrorGuard';
import { UserService } from '@core/services/user/UserService';
import { getConfig } from '@superbet-group/web.lib.config';

interface LimitsState {
    [LimitType.Deposit]: LimitData;
    [LimitType.TotalWager]: LimitData;
    [LimitType.Loss]: LimitData;
    [LimitType.Session]: LimitData;
    realityCheckInterval :number;
}

const initialState: LimitsState = {
    [LimitType.Deposit]: {
        month: null,
        week: null,
        day: null,
    },
    [LimitType.TotalWager]: {
        month: null,
        week: null,
        day: null,
    },
    [LimitType.Loss]: {
        month: null,
        week: null,
        day: null,
    },
    [LimitType.Session]: {
        month: null,
        week: null,
        day: null,
    },
    realityCheckInterval: 0,
};

const SET_DEPOSIT_LIMITS = 'SET_DEPOSIT_LIMITS';
const SET_TOTAL_WAGER_LIMITS = 'SET_TOTAL_WAGER_LIMITS';
const SET_LOSS_LIMITS = 'SET_LOSS_LIMITS';
const SET_SESSION_LIMITS = 'SET_SESSION_LIMITS';
const SET_REALITY_CHECK_INTERVAL = 'SET_REALITY_CHECK';

function logout(dispatch: Dispatch) {
    dispatch('data/authentication/logout', null, { root: true });
}

const limitsStore = {
    state: initialState,
    getters: {
        depositLimits: createSimpleGetter<LimitsState, typeof LimitType.Deposit>(LimitType.Deposit),
        totalWagerLimits: createSimpleGetter<LimitsState, typeof LimitType.TotalWager>(LimitType.TotalWager),
        lossLimits: createSimpleGetter<LimitsState, typeof LimitType.Loss>(LimitType.Loss),
        sessionLimits: createSimpleGetter<LimitsState, typeof LimitType.Session>(LimitType.Session),
        realityCheckPeriod: createSimpleGetter<LimitsState, 'realityCheckInterval'>('realityCheckInterval'),
    },
    mutations: {
        [SET_DEPOSIT_LIMITS]: createSimpleMutator<LimitsState, typeof LimitType.Deposit>(LimitType.Deposit),
        [SET_TOTAL_WAGER_LIMITS]: createSimpleMutator<LimitsState, typeof LimitType.TotalWager>(LimitType.TotalWager),
        [SET_LOSS_LIMITS]: createSimpleMutator<LimitsState, typeof LimitType.Loss>(LimitType.Loss),
        [SET_SESSION_LIMITS]: createSimpleMutator<LimitsState, typeof LimitType.Session>(LimitType.Session),
        [SET_REALITY_CHECK_INTERVAL]: createSimpleMutator<any, 'realityCheckInterval'>('realityCheckInterval'),
    },
    actions: {
        setLimits: asyncErrorGuard((
            context: ActionContext<LimitsState, unknown>,
            data: {
                limits: {
                    month?: number;
                    week?: number;
                    day?: number;
                };
                limitType: LimitType;
            },
        ) => {
            const limitsToSet = [];
            const {
                limitType,
                limits: {
                    month,
                    week,
                    day,
                },
            } = data;

            if (typeof month === 'number') {
                limitsToSet.push({
                    limitType,
                    amount: month,
                    time: TimeType.Month,
                });
            }
            if (typeof week === 'number') {
                limitsToSet.push({
                    limitType,
                    amount: week,
                    time: TimeType.Week,
                });
            }
            if (typeof day === 'number') {
                limitsToSet.push({
                    limitType,
                    amount: day,
                    time: TimeType.Day,
                });
            }

            return setLimits(limitsToSet);
        }),

        async getLimits({ commit, dispatch }: { commit: Commit, dispatch: Dispatch }) {
            const limits = await getLimits();

            if (limits[LimitType.Deposit]) {
                commit(SET_DEPOSIT_LIMITS, limits[LimitType.Deposit]);
            }
            if (limits[LimitType.TotalWager]) {
                commit(SET_TOTAL_WAGER_LIMITS, limits[LimitType.TotalWager]);
            }
            if (limits[LimitType.Loss]) {
                commit(SET_LOSS_LIMITS, limits[LimitType.Loss]);
            }
            if (limits[LimitType.Session]) {
                commit(SET_SESSION_LIMITS, limits[LimitType.Session]);
            }

            dispatch('fetchRealityCheckPeriod');
        },

        cancelLimits: asyncErrorGuard(async ({ dispatch }: { dispatch: Dispatch }, limitsIds: number[]) => {
            await Promise.all(limitsIds.map(cancelLimit));
            dispatch('getLimits');
        }),

        selfExclude: asyncErrorGuard(async ({ dispatch }: { dispatch: Dispatch }, endDate: Date) => {
            await selfExclude(endDate);
            dispatch('ui/modal/clearModals', null, { root: true });
            logout(dispatch);
        }),

        timeOut: asyncErrorGuard(async ({ dispatch }: { dispatch: Dispatch }, endDate: Date) => {
            await timeOut(endDate);
            dispatch('ui/modal/clearModals', null, { root: true });
            logout(dispatch);
        }),
        closeAccount: asyncErrorGuard(async ({ dispatch }: { dispatch: Dispatch }, reasonPayload?: ReasonPayload) => {
            await closeAccount(reasonPayload);

            dispatch('ui/modal/clearModals', null, { root: true });
            logout(dispatch);
        }),
        closeAccountMfa: asyncErrorGuard(async ({ dispatch }: { dispatch: Dispatch }, payload: CloseAccountMfaPayload) => {
            await closeAccountMfa(payload);

            dispatch('ui/modal/clearModals', null, { root: true });
            logout(dispatch);
        }),
        fetchRealityCheckPeriod: asyncErrorGuard(async ({ commit }: { commit: Commit }) => {
            if (getConfig().countrySpecific.user.responsibleGaming?.realityCheck) {
                const response = await UserService.getInstance().getPlayerSettings('realityCheck');
                commit(SET_REALITY_CHECK_INTERVAL, response.data?.SettingValue?.minute);
            }
        }),
        setRealityCheckPeriod: asyncErrorGuard(async ({ commit }: { commit: Commit }, period :{ minute: number }) => {
            const minutesInPeriod = typeof period === 'number' ? period : period.minute;
            await UserService.getInstance().setPlayerSettings('realityCheck', { minute: minutesInPeriod });
            commit(SET_REALITY_CHECK_INTERVAL, minutesInPeriod);
        }),

    },
};

export type LimitsStore = typeof limitsStore;

export default limitsStore;
